import { useId, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useDeployStack, useNodes } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";
import "./stacks.css";

interface DeployStackModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

const COMPOSE_PLACEHOLDER = `services:
  web:
    image: nginx:1.27
    ports:
      - "8080:80"
`;

/**
 * Deploys a Compose file to one node in the fleet (charter C-9). The compose
 * text stays canonical on disk server-side once deployed — this form is a way
 * to hand it to the control plane, not a place it's authored/edited going
 * forward.
 */
export function DeployStackModal({ open, fleetId, onClose }: DeployStackModalProps) {
  const nameId = useId();
  const nodeFieldId = useId();
  const composeId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const mutation = useDeployStack(fleetId);
  const { data: nodesPage, isPending: nodesPending } = useNodes(fleetId);

  const [name, setName] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [compose, setCompose] = useState("");

  const nodes = nodesPage?.items ?? [];

  function handleClose() {
    setName("");
    setNodeId("");
    setCompose("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    mutation.mutate(
      { name: submittedName, nodeId, compose },
      {
        onSuccess: (stack) => {
          pushToast({ title: `Deploying "${stack.name}"…`, tone: "success" });
          handleClose();
          navigate(`/stacks/${stack.id}`);
        },
        // Deliberately no toast here: a validation/conflict/node-unavailable
        // message (the common case is a compose field the server named) is
        // shown inline below instead, where it stays legible while the
        // operator fixes the input rather than fading out after a few
        // seconds.
      },
    );
  }

  const canSubmit =
    name.trim().length > 0 && nodeId.length > 0 && compose.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="Deploy a stack" onClose={handleClose} initialFocusId={nameId} size="lg">
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't deploy this stack."}
          </p>
        )}

        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={63}
          />
        </label>

        <label className="form-field" htmlFor={nodeFieldId}>
          <span>Target node</span>
          <select id={nodeFieldId} value={nodeId} onChange={(e) => setNodeId(e.target.value)} required disabled={nodesPending}>
            <option value="" disabled>
              {nodesPending ? "Loading nodes…" : "Select a node"}
            </option>
            {nodes.map((node) => (
              <option key={node.id} value={node.id}>
                {node.name}
                {node.status !== "online" ? ` (${node.status})` : ""}
              </option>
            ))}
          </select>
          {!nodesPending && nodes.length === 0 && (
            <span className="form-field__hint">No nodes enrolled in this fleet yet — add a host first.</span>
          )}
        </label>

        <label className="form-field stacks-compose-field" htmlFor={composeId}>
          <span>Compose file</span>
          <textarea
            id={composeId}
            className="stacks-compose-input"
            value={compose}
            onChange={(e) => setCompose(e.target.value)}
            placeholder={COMPOSE_PLACEHOLDER}
            spellCheck={false}
            wrap="off"
            rows={16}
            required
          />
          <span className="form-field__hint">
            A bounded Compose subset (charter C-9). An unsupported field is refused by name rather than silently
            dropped — that error will name the field here if you hit it.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Deploying…" : "Deploy stack"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
